// src/pages/carbon-offset-dashboard/components/OffsetPurchaseModal.jsx
import React, { useState, useEffect } from 'react';
import Icon from 'components/AppIcon';

const OffsetPurchaseModal = ({ shipment, eligibleShipments, onClose, onPurchaseComplete }) => {
  const [selectedShipment, setSelectedShipment] = useState(shipment || null);
  const [offsetType, setOffsetType] = useState('forestry');
  const [offsetAmount, setOffsetAmount] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [step, setStep] = useState(1);
  const [formErrors, setFormErrors] = useState({});

  const offsetTypes = [
    {
      id: 'forestry',
      name: 'Forestry & Land Use',
      description: 'Support reforestation and forest conservation projects',
      pricePerTonne: 18,
      verificationStandard: 'VCS',
      deliveryTime: '3-6 months',
      projects: ['Amazon Rainforest Conservation', 'Sustainable Forestry Brazil'],
      impact: 'High biodiversity protection'
    },
    {
      id: 'renewable',
      name: 'Renewable Energy',
      description: 'Wind, solar, and hydroelectric power projects',
      pricePerTonne: 22,
      verificationStandard: 'Gold Standard',
      deliveryTime: '1-3 months',
      projects: ['Wind Farm Project Mexico', 'Solar Installation India'],
      impact: 'Clean energy transition'
    },
    {
      id: 'blue-carbon',
      name: 'Blue Carbon',
      description: 'Coastal ecosystem restoration and protection',
      pricePerTonne: 25,
      verificationStandard: 'VCS',
      deliveryTime: '6-12 months',
      projects: ['Mangrove Restoration Indonesia', 'Seagrass Protection Australia'],
      impact: 'Marine ecosystem protection'
    },
    {
      id: 'direct-air-capture',
      name: 'Direct Air Capture',
      description: 'Technology-based carbon removal from atmosphere',
      pricePerTonne: 35,
      verificationStandard: 'Gold Standard',
      deliveryTime: '1-2 months',
      projects: ['Climeworks DAC Facility', 'Carbon Engineering Plant'],
      impact: 'Permanent carbon removal'
    }
  ];

  useEffect(() => {
    if (selectedShipment && selectedShipment.carbonFootprint?.total) {
      setOffsetAmount(selectedShipment.carbonFootprint.total.toString());
    }
  }, [selectedShipment]);

  const selectedOffsetType = offsetTypes.find(type => type.id === offsetType);
  const totalCost = parseFloat(offsetAmount || 0) * (selectedOffsetType?.pricePerTonne || 0);

  const validateForm = () => {
    const errors = {};
    
    if (!selectedShipment) {
      errors.shipment = 'Please select a shipment';
    }
    
    if (!offsetAmount || parseFloat(offsetAmount) <= 0) {
      errors.offsetAmount = 'Please enter a valid offset amount';
    }
    
    if (selectedShipment && parseFloat(offsetAmount) > selectedShipment.carbonFootprint?.total * 1.1) {
      errors.offsetAmount = 'Offset amount cannot exceed 110% of shipment emissions';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = () => {
    if (step === 1) {
      if (validateForm()) {
        setStep(2);
      }
    } else if (step === 2) {
      setStep(3);
    }
  };

  const handlePurchase = () => {
    setIsProcessing(true);
    
    // Simulate API call
    setTimeout(() => {
      const offsetData = {
        shipmentId: selectedShipment.trackingId,
        customer: selectedShipment.customer,
        amount: parseFloat(offsetAmount),
        cost: totalCost,
        offsetType: selectedOffsetType.name,
        certificateId: `VCS-${Date.now()}-${Math.random().toString(36).substr(2, 6).toUpperCase()}`,
        status: 'pending',
        project: selectedOffsetType.projects[0],
        purchaseDate: new Date()
      };
      
      onPurchaseComplete(offsetData);
      setIsProcessing(false);
    }, 2000);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-modal p-4">
      <div className="bg-surface rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-success-50 rounded-lg">
              <Icon name="Leaf" size={24} className="text-success" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-text-primary">
                Purchase Carbon Offset
              </h2>
              <p className="text-sm text-text-secondary">
                Step {step} of 3: {step === 1 ? 'Select Shipment & Amount' : step === 2 ? 'Choose Offset Type' : 'Review & Purchase'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isProcessing}
            className="p-2 text-text-secondary hover:text-primary hover:bg-primary-50 rounded-md transition-colors duration-150 disabled:opacity-50"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="px-6 py-4 border-b border-border">
          <div className="flex items-center space-x-4">
            {[1, 2, 3].map((stepNumber) => (
              <div key={stepNumber} className="flex items-center">
                <div className={`
                  w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors duration-150
                  ${
                    stepNumber <= step
                      ? 'bg-primary text-white' :'bg-surface-100 text-text-secondary'
                  }
                `}>
                  {stepNumber < step ? <Icon name="Check" size={16} /> : stepNumber}
                </div>
                {stepNumber < 3 && (
                  <div className={`
                    w-16 h-1 mx-2 rounded
                    ${
                      stepNumber < step
                        ? 'bg-primary' :'bg-surface-100'
                    }
                  `} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {step === 1 && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-3">
                  Select Shipment
                </label>
                <div className="space-y-3">
                  {eligibleShipments.map((ship) => (
                    <div
                      key={ship.id}
                      onClick={() => setSelectedShipment(ship)}
                      className={`
                        p-4 border rounded-lg cursor-pointer transition-all duration-150
                        ${
                          selectedShipment?.id === ship.id
                            ? 'border-primary bg-primary-50' :'border-border hover:border-primary-200 hover:bg-surface-50'
                        }
                      `}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className={`
                            w-4 h-4 rounded-full border-2 flex items-center justify-center
                            ${
                              selectedShipment?.id === ship.id
                                ? 'border-primary bg-primary' :'border-border'
                            }
                          `}>
                            {selectedShipment?.id === ship.id && (
                              <Icon name="Check" size={10} className="text-white" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-text-primary">{ship.trackingId}</p>
                            <p className="text-sm text-text-secondary">{ship.customer}</p>
                            <p className="text-xs text-text-secondary mt-1">{ship.route}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-medium text-text-primary">
                            {ship.carbonFootprint.total} {ship.carbonFootprint.unit}
                          </p>
                          <p className="text-sm text-text-secondary">
                            Est. {formatCurrency(ship.estimatedOffsetCost)}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {formErrors.shipment && (
                  <p className="text-sm text-error mt-2">{formErrors.shipment}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Offset Amount (tonnes CO2e)
                </label>
                <input
                  type="number"
                  value={offsetAmount}
                  onChange={(e) => setOffsetAmount(e.target.value)}
                  step="0.1"
                  min="0"
                  max={selectedShipment?.carbonFootprint?.total * 1.1 || undefined}
                  className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Enter offset amount"
                />
                {selectedShipment && (
                  <p className="text-sm text-text-secondary mt-2">
                    Shipment emissions: {selectedShipment.carbonFootprint.total} tonnes CO2e
                  </p>
                )}
                {formErrors.offsetAmount && (
                  <p className="text-sm text-error mt-2">{formErrors.offsetAmount}</p>
                )}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-text-primary mb-4">
                  Choose Offset Type
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {offsetTypes.map((type) => (
                    <div
                      key={type.id}
                      onClick={() => setOffsetType(type.id)}
                      className={`
                        p-4 border rounded-lg cursor-pointer transition-all duration-150
                        ${
                          offsetType === type.id
                            ? 'border-primary bg-primary-50' :'border-border hover:border-primary-200 hover:bg-surface-50'
                        }
                      `}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className={`
                          w-4 h-4 rounded-full border-2 flex items-center justify-center mt-1
                          ${
                            offsetType === type.id
                              ? 'border-primary bg-primary' :'border-border'
                          }
                        `}>
                          {offsetType === type.id && (
                            <Icon name="Check" size={10} className="text-white" />
                          )}
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-text-primary">
                            {formatCurrency(type.pricePerTonne)}/tonne
                          </p>
                          <p className="text-xs text-text-secondary">
                            {type.verificationStandard}
                          </p>
                        </div>
                      </div>
                      <h4 className="font-medium text-text-primary mb-2">{type.name}</h4>
                      <p className="text-sm text-text-secondary mb-3">{type.description}</p>
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <Icon name="Clock" size={14} className="text-text-secondary" />
                          <span className="text-xs text-text-secondary">{type.deliveryTime}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Icon name="Target" size={14} className="text-text-secondary" />
                          <span className="text-xs text-text-secondary">{type.impact}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6">
              <div className="bg-surface-50 rounded-lg p-6 border border-border-light">
                <h3 className="text-lg font-medium text-text-primary mb-4">
                  Purchase Summary
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-text-secondary">Shipment:</span>
                      <span className="text-sm font-medium text-text-primary">{selectedShipment?.trackingId}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-text-secondary">Customer:</span>
                      <span className="text-sm font-medium text-text-primary">{selectedShipment?.customer}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-text-secondary">Offset Amount:</span>
                      <span className="text-sm font-medium text-text-primary">{offsetAmount} tonnes CO2e</span>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-text-secondary">Offset Type:</span>
                      <span className="text-sm font-medium text-text-primary">{selectedOffsetType?.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-text-secondary">Price per tonne:</span>
                      <span className="text-sm font-medium text-text-primary">{formatCurrency(selectedOffsetType?.pricePerTonne || 0)}</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t border-border">
                      <span className="text-sm font-medium text-text-primary">Total Cost:</span>
                      <span className="text-lg font-bold text-primary">{formatCurrency(totalCost)}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-primary-50 rounded-lg p-4 border border-primary-100">
                <div className="flex items-start space-x-2">
                  <Icon name="Info" size={16} className="text-primary mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-primary mb-1">Purchase Terms</p>
                    <ul className="text-xs text-primary space-y-1">
                      <li>• Carbon credits will be issued within {selectedOffsetType?.deliveryTime}</li>
                      <li>• Verified by {selectedOffsetType?.verificationStandard} standard</li>
                      <li>• Digital certificate will be provided upon verification</li>
                      <li>• This offset will be permanently retired to avoid double counting</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border">
          <div className="flex items-center space-x-4">
            {step > 1 && (
              <button
                onClick={() => setStep(step - 1)}
                disabled={isProcessing}
                className="px-4 py-2 text-text-secondary hover:text-primary border border-border rounded-md hover:bg-surface-50 transition-colors duration-150 disabled:opacity-50"
              >
                Back
              </button>
            )}
          </div>
          
          <div className="flex items-center space-x-4">
            {step < 3 ? (
              <button
                onClick={handleNext}
                disabled={!selectedShipment || !offsetAmount}
                className="px-6 py-2 bg-primary text-white rounded-md hover:bg-primary-600 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            ) : (
              <button
                onClick={handlePurchase}
                disabled={isProcessing}
                className="px-6 py-2 bg-success text-white rounded-md hover:bg-success-600 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {isProcessing ? (
                  <>
                    <Icon name="Loader" size={16} className="animate-spin" />
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <Icon name="CreditCard" size={16} />
                    <span>Purchase Offset</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OffsetPurchaseModal;